/** Session player entry point — orchestrates step display, controls, animations, and completion flow. */
import { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { motion } from 'motion/react';
import type { StudySessionStep, GradingPhaseEvent } from '@formency/shared';
import type { UnifiedStepResult } from '@/shared/api/hooks';

const EASE = [0.16, 1, 0.3, 1] as const;
import { SessionProvider, useSession } from './SessionContext';
import { SegmentedProgressBar } from './components/SegmentedProgressBar';
import { SessionControls } from './components/SessionControls';
import { SessionToolbar } from './components/SessionToolbar';
import {
  ShortcutOverlayProvider,
  ShortcutLegend,
  useShortcutOverlay,
} from './components/ShortcutOverlay';
import { KeyboardRippleProvider } from './components/KeyboardRipple';
import { CorrectStreak } from './components/CorrectStreak';
import { ConfidenceCheck } from './components/ConfidenceCheck';
import type { ConfidenceLevel } from './components/ConfidenceCheck';
import { ReadinessBreakModal } from './components/ReadinessBreakModal';
import { PaasPrompt } from './components/PaasPrompt';
import { useBreakModalGate } from './components/useBreakModalGate';
import { useInvalidateReadiness } from '@/shared/api/hooks/useReadiness';
import { StepRouter } from './steps/StepRouter';
import { SessionCompletionScreen } from './SessionCompletionScreen';
import { useSubmitCeremony } from './hooks/useSubmitCeremony';
import { useStepTimer } from './hooks/useStepTimer';
import { useBehavioralTracker } from './hooks/useBehavioralTracker';
import { useStepKeyboard } from './hooks/useStepKeyboard';
import { useSwipeNavigation } from './hooks/useSwipeNavigation';
import { useEnforcedReading } from './hooks/useEnforcedReading';
import { useSessionSound } from './useSessionSound';
import type { SessionMode, CompletionMetrics, StepAnswer, StepResult } from './types';
import { calculateCompletionMetrics } from './sessionUtils';

const CONFIDENCE_MAP: Record<ConfidenceLevel, number> = {
  low: 0.25,
  medium: 0.5,
  high: 0.75,
};

// ─── Types ──────────────────────────────────────────────────────────────────

/** Submit function signature — convert answer → API result. */
export type SessionSubmitFn = (
  sessionId: string,
  position: number,
  answer: StepAnswer,
  onGradingPhase?: (event: GradingPhaseEvent) => void,
  responseTimeMs?: number,
  confidence?: number,
  behavioral?: {
    readingMs?: number;
    answeringMs?: number;
    revisionCount?: number;
    initialResponseJson?: unknown;
    focusLossCount?: number;
    idleCount?: number;
  },
  /**
   * Paas mental-effort rating (1-9). Sent only when the placement assigned
   * a Paas prompt to this step (per spec §7.4). Server validates the
   * placement before persisting; spurious values are silently dropped.
   */
  paasEffort?: number
) => Promise<{ result: StepResult; response: unknown; raw: UnifiedStepResult }>;

interface SessionPlayerProps {
  sessionId: string;
  steps: StudySessionStep[];
  mode: SessionMode;
  startIndex?: number;
  confidencePositions?: number[];
  paasPositions?: number[];
  onExit: () => void;
  onComplete?: (metrics: CompletionMetrics) => void;
  submitFn: SessionSubmitFn;
  onNextLesson?: () => void;
  onRetry?: () => void;
  onReviseLesson?: () => void;
  revisingLesson?: boolean;
  isPersonalLesson?: boolean;
  /** Pass percentage for the lesson (null = no pass mark). */
  passPct?: number | null;
}

// ─── Inner component (uses context hooks) ───────────────────────────────────

interface SessionPlayerInnerProps {
  onExit: () => void;
  onComplete?: (metrics: CompletionMetrics) => void;
  submitFn: SessionSubmitFn;
  confidencePositions?: number[];
  paasPositions?: number[];
  onNextLesson?: () => void;
  onRetry?: () => void;
  onReviseLesson?: () => void;
  revisingLesson?: boolean;
  isPersonalLesson?: boolean;
  passPct?: number | null;
}

function SessionPlayerInner({
  onExit,
  onComplete,
  submitFn,
  confidencePositions,
  paasPositions,
  onNextLesson,
  onRetry,
  onReviseLesson,
  revisingLesson,
  isPersonalLesson,
  passPct,
}: SessionPlayerInnerProps) {
  const { state, currentStep } = useSession();
  const { startStep, stopAndFlush, getActiveTimeMs, getPhaseTimesMs, markInteraction, markGraded } =
    useStepTimer(state.sessionId);
  const { recordAnswerChange, getMetrics: getBehavioralMetrics } = useBehavioralTracker(
    state.currentIndex
  );
  const { submit, submitInfoStep, setConfidence, setPaasEffort } = useSubmitCeremony({
    submitFn,
    getActiveTimeMs,
    getPhaseTimesMs,
    getBehavioralMetrics,
  });

  const onConfidenceSelect = useCallback(
    (level: ConfidenceLevel) => {
      setConfidence(CONFIDENCE_MAP[level]);
    },
    [setConfidence]
  );

  const onPaasSelect = useCallback(
    (effort: number) => {
      setPaasEffort(effort);
    },
    [setPaasEffort]
  );
  const { playComplete } = useSessionSound();
  const hasCalledComplete = useRef(false);
  const hadAnswerRef = useRef(false);

  // Track step viewing time — keyed on currentIndex so back-navigation re-triggers.
  // Uses sourceStepId (the canonical step ID the backend maps against).
  const isAlreadyAnswered = state.answeredSteps.has(state.currentIndex);
  useEffect(() => {
    const sid = currentStep?.sourceStepId;
    if (sid) startStep(sid, isAlreadyAnswered ? 'reviewing' : 'reading');
    hadAnswerRef.current = isAlreadyAnswered;
  }, [state.currentIndex, currentStep, startStep, isAlreadyAnswered]);

  // Phase transition: reading → answering (first UPDATE_ANSWER on a fresh step)
  useEffect(() => {
    if (state.answer && !hadAnswerRef.current) {
      hadAnswerRef.current = true;
      markInteraction();
    }
  }, [state.answer, markInteraction]);

  // Track answer revisions for behavioral metrics
  useEffect(() => {
    if (state.answer && state.phase === 'answering') {
      recordAnswerChange(state.answer);
    }
  }, [state.answer, state.phase, recordAnswerChange]);

  // Phase transition: answering → reviewing (grading result received)
  useEffect(() => {
    if (state.phase === 'graded') {
      markGraded();
    }
  }, [state.phase, markGraded]);

  // Calculate metrics from answered steps (excluding info steps)
  const metrics = useMemo<CompletionMetrics>(() => {
    const scoredSteps: { stepKind: string; score: number; maxScore: number }[] = [];

    state.answeredSteps.forEach((answered, index) => {
      const step = state.steps[index];
      scoredSteps.push({
        stepKind: step?.stepKind ?? 'info',
        score: answered.result.score,
        maxScore: answered.result.maxScore,
      });
    });

    return calculateCompletionMetrics(scoredSteps);
  }, [state.answeredSteps, state.steps]);

  // Flush step time and mark session complete as soon as isComplete becomes true
  useEffect(() => {
    if (state.isComplete && !hasCalledComplete.current) {
      hasCalledComplete.current = true;
      stopAndFlush();
      onComplete?.(metrics);
    }
  }, [state.isComplete, onComplete, metrics, stopAndFlush]);

  // Show completion screen when session is done
  if (state.isComplete) {
    return (
      <SessionCompletionScreen
        metrics={metrics}
        mode={state.mode}
        playComplete={playComplete}
        onExit={onExit}
        onNextLesson={onNextLesson}
        onRetry={onRetry}
        onReviseLesson={onReviseLesson}
        revisingLesson={revisingLesson}
        isPersonalLesson={isPersonalLesson}
        passPct={passPct}
      />
    );
  }

  return (
    <ShortcutOverlayProvider currentIndex={state.currentIndex}>
      <KeyboardRippleProvider>
        <SessionPlayerContent
          onExit={onExit}
          onSubmit={submit}
          onSubmitInfo={submitInfoStep}
          confidencePositions={confidencePositions}
          onConfidenceSelect={onConfidenceSelect}
          paasPositions={paasPositions}
          onPaasSelect={onPaasSelect}
        />
      </KeyboardRippleProvider>
    </ShortcutOverlayProvider>
  );
}

/** Exit animation — mirrors the entrance: shells slide off, content fades. */
const EXIT_DURATION = 0.35;

/** Rendered inside ShortcutOverlayProvider so it can access the overlay. */
function SessionPlayerContent({
  onExit,
  onSubmit,
  onSubmitInfo,
  confidencePositions,
  onConfidenceSelect,
  paasPositions,
  onPaasSelect,
}: {
  onExit: () => void;
  onSubmit: () => void;
  onSubmitInfo: () => void;
  confidencePositions?: number[];
  onConfidenceSelect: (level: ConfidenceLevel) => void;
  paasPositions?: number[];
  onPaasSelect: (effort: number) => void;
}) {
  const { state, currentStep } = useSession();
  const { togglePin } = useShortcutOverlay();
  const [showConfidence, setShowConfidence] = useState(false);
  const [showPaas, setShowPaas] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const invalidateReadiness = useInvalidateReadiness();

  // Intercept exit to play animation first, then call the real onExit
  const requestExit = useCallback(() => {
    if (!isExiting) setIsExiting(true);
    // Invalidate the dashboard readiness query so the home screen reflects
    // the post-session state immediately (per spec §10.4 cache-invalidation).
    invalidateReadiness();
  }, [isExiting, invalidateReadiness]);

  // Break-modal gate — fires the modal when score crosses threshold and all
  // §11.1 preconditions pass. ONLY evaluates on step-submit-derived readiness
  // (state.readiness is null until the first question submission).
  //
  // Pass `episodeStartedAtMs` (a stable timestamp) rather than the derived
  // minutes — the gate computes elapsed time inside its own evaluation logic
  // where Date.now() is contained and only read at gate-evaluation time.
  const sessionType = state.mode;
  // All-steps total (questions AND info), unit-matched against `state.currentIndex`
  // for the last-N-steps suppression in useBreakModalGate. Previously this passed
  // only the question count — a unit mismatch that caused the modal to be silently
  // suppressed across the back half of any lesson with info steps interleaved.
  const totalSteps = state.steps.length;
  const breakGate = useBreakModalGate(
    {
      readiness: state.readiness ?? undefined,
      responseCount: state.readiness?.responseCount ?? 0,
      episodeStartedAtMs: state.readiness?.episodeStartedAtMs ?? null,
      sessionType,
      totalSteps,
      currentPosition: state.currentIndex,
      lastModalDismissedAtMs: state.readiness?.lastModalDismissedAtMs ?? null,
    },
    () => {
      // Take-a-break path: invalidate dashboard readiness then exit the session.
      invalidateReadiness();
      if (!isExiting) setIsExiting(true);
    }
  );

  const shouldShowConfidence = useCallback(() => {
    if (currentStep?.stepKind === 'info') return false;
    if (!confidencePositions || confidencePositions.length === 0) return false;
    return confidencePositions.includes(state.currentIndex);
  }, [state.currentIndex, currentStep?.stepKind, confidencePositions]);

  // Whether to show the Paas overlay before submitting this step's response.
  // Per spec §7.2: confidence and Paas never share a position, so confidence
  // takes precedence if both somehow ended up on the same step (defensive).
  const shouldShowPaas = useCallback(() => {
    if (currentStep?.stepKind === 'info') return false;
    if (!paasPositions || paasPositions.length === 0) return false;
    if (shouldShowConfidence()) return false;
    return paasPositions.includes(state.currentIndex);
  }, [state.currentIndex, currentStep?.stepKind, paasPositions, shouldShowConfidence]);

  const handleSubmit = useCallback(() => {
    if (shouldShowConfidence()) {
      setShowConfidence(true);
    } else if (shouldShowPaas()) {
      setShowPaas(true);
    } else {
      onSubmit();
    }
  }, [shouldShowConfidence, shouldShowPaas, onSubmit]);

  const handleConfidenceSelect = useCallback(
    (level: ConfidenceLevel) => {
      onConfidenceSelect(level);
      setShowConfidence(false);
      onSubmit();
    },
    [onConfidenceSelect, onSubmit]
  );

  const handlePaasSelect = useCallback(
    (effort: number) => {
      onPaasSelect(effort);
      setShowPaas(false);
      onSubmit();
    },
    [onPaasSelect, onSubmit]
  );

  const { blockNext } = useEnforcedReading();

  useStepKeyboard({
    onSubmit: handleSubmit,
    onSubmitInfo,
    onExit: requestExit,
    onToggleShortcuts: togglePin,
    disabled: showConfidence || showPaas || isExiting,
    blockNext,
  });

  useSwipeNavigation({ onSubmitInfo, blockNext });

  // Entrance vs exit animation targets
  const entered = !isExiting;

  return (
    <motion.div
      className="relative flex h-dvh flex-col"
      style={{ background: 'var(--f-bg)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: entered ? 1 : 0 }}
      transition={{ duration: entered ? 0.25 : EXIT_DURATION, ease: EASE }}
      onAnimationComplete={() => {
        if (isExiting) onExit();
      }}
    >
      {/* Header: progress bar + streak + toolbar (fixed) */}
      <motion.div
        className="shrink-0 flex items-center gap-3 px-4 pt-4 pb-2 sm:px-8"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: entered ? 1 : 0, y: entered ? 0 : '-100%' }}
        transition={{
          duration: entered ? 0.45 : EXIT_DURATION,
          delay: entered ? 0.1 : 0,
          ease: EASE,
        }}
      >
        <div className="min-w-0 flex-1" style={{ transition: 'flex 0.4s ease' }}>
          <SegmentedProgressBar />
        </div>
        <CorrectStreak />
        <SessionToolbar onExit={requestExit} />
      </motion.div>

      {/* Content area: step router (centred, constrained width) */}
      <motion.div
        className="relative flex min-h-0 flex-1 flex-col items-center overflow-y-auto px-4 py-6 sm:px-8"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: entered ? 1 : 0, y: 0 }}
        transition={{
          duration: entered ? 0.45 : EXIT_DURATION,
          delay: entered ? 0.18 : 0,
          ease: EASE,
        }}
      >
        <div className="flex w-full max-w-3xl flex-1 flex-col lg:max-w-[52rem]">
          <StepRouter />
        </div>
      </motion.div>

      {/* Confidence check overlay */}
      <ConfidenceCheck show={showConfidence} onSelect={handleConfidenceSelect} />

      {/* Paas mental-effort overlay — appears for non-cold-start users on
          step positions that the placement assigned a Paas prompt to. The
          selected value rides on the next step submission as `paasEffort`
          and is persisted server-side after the placement-assigned check. */}
      {showPaas && (
        <div
          className="fixed inset-0 z-40 flex items-center justify-center px-4"
          style={{ background: 'rgba(0, 0, 0, 0.32)' }}
        >
          <div className="w-full max-w-md">
            <PaasPrompt
              show={showPaas}
              onSelect={handlePaasSelect}
              sessionId={state.sessionId}
              stepPosition={state.currentIndex}
              algorithmVersion={state.readiness?.algorithmVersion}
            />
          </div>
        </div>
      )}

      {/* Readiness break-recommendation modal — fires when the score drops
          below threshold AND all §11.1 preconditions pass. */}
      <ReadinessBreakModal
        show={breakGate.showModal}
        onTakeBreak={breakGate.dismissBreak}
        onKeepGoing={breakGate.dismissContinue}
        sessionId={state.sessionId}
        score={state.readiness?.score}
        bars={state.readiness?.bars}
        algorithmVersion={state.readiness?.algorithmVersion}
      />

      {/* Footer: legend + action button */}
      <motion.div
        className="sticky bottom-0 flex flex-col border-t px-4 py-4 sm:px-8"
        style={{ borderColor: 'var(--f-border)', background: 'var(--f-bg)' }}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: entered ? 1 : 0, y: entered ? 0 : '100%' }}
        transition={{
          duration: entered ? 0.45 : EXIT_DURATION,
          delay: entered ? 0.25 : 0,
          ease: EASE,
        }}
      >
        <ShortcutLegend />
        <SessionControls
          onSubmit={handleSubmit}
          onSubmitInfo={onSubmitInfo}
          blockNext={blockNext}
        />
      </motion.div>
    </motion.div>
  );
}

// ─── Public component ───────────────────────────────────────────────────────

export function SessionPlayer({
  sessionId,
  steps,
  mode,
  startIndex,
  confidencePositions,
  paasPositions,
  onExit,
  onComplete,
  submitFn,
  onNextLesson,
  onRetry,
  onReviseLesson,
  revisingLesson,
  isPersonalLesson,
  passPct,
}: SessionPlayerProps) {
  return (
    <SessionProvider sessionId={sessionId} steps={steps} mode={mode} startIndex={startIndex}>
      <SessionPlayerInner
        onExit={onExit}
        onComplete={onComplete}
        submitFn={submitFn}
        confidencePositions={confidencePositions}
        paasPositions={paasPositions}
        onNextLesson={onNextLesson}
        onRetry={onRetry}
        onReviseLesson={onReviseLesson}
        revisingLesson={revisingLesson}
        isPersonalLesson={isPersonalLesson}
        passPct={passPct}
      />
    </SessionProvider>
  );
}
